'use client';

import { ReactNode, useEffect, useMemo, useState } from 'react';

import {
    Box,
    BoxContent,
    ButtonVariant,
    FlexItem,
    Text,
    TextLevels,
    TextMarkup,
    TrilogyColor,
} from '@bytel/trilogy-react';
import { ButtonProps } from '@bytel/trilogy-react/lib/components/button/ButtonProps';

import { filterPromotionsByTypesMarketing } from '@services/products';

import { DiscountType } from '@app-types/discount';
import { MobilePlanType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';
import { ModalPlanDetails } from '@components/product-details-modal';
import { PromoHomeSticker } from '@components/promo-home-sticker';

import { getPricesWithPromos } from '@helpers/offer-wall';
import { capitalize } from '@helpers/wording';

import { MarketingType } from '@constants/plan';

import { OfferCardPepites } from 'src/app/(offer-wall)/_components/offer-card-pepites';

import { getPromoDuration } from '../_helpers/product';
import { PlanConfigurator } from './plan-configurator';

type MobileCardProps = {
    plans: MobilePlanType[];
    index: number;
    initialGencode?: string;
    currentSelected?: MobilePlanType;
    onSelectedPlan?: (plan: MobilePlanType) => void;
    onRemovePlan: () => void;
    fullHeight: boolean;
};

export function MobileCard({
    plans,
    index,
    onSelectedPlan,
    onRemovePlan,
    currentSelected,
    fullHeight,
}: Readonly<MobileCardProps>) {
    const plansWithPrices = useMemo(
        () =>
            plans.map((plan) => ({
                ...plan,
                details: {
                    ...plan.details,
                    price: getPricesWithPromos(plan.details),
                },
            })),
        [plans],
    );

    const defaultPlan = useMemo(
        () => plansWithPrices.find((plan) => plan.isDefault) ?? plansWithPrices[0]!,
        [plansWithPrices],
    );
    const [selectedPlan, setSelectedPlan] = useState<MobilePlanType>(defaultPlan);
    const [shouldShowModal, setShouldShowModal] = useState(false);

    useEffect(() => {
        if (plansWithPrices && (currentSelected || selectedPlan)) {
            const gencodeToCheck = selectedPlan?.gencode || currentSelected?.gencode;
            const plan = plansWithPrices.find((p) => p.gencode === gencodeToCheck);
            if (plan && plan.details?.price?.final !== (selectedPlan || currentSelected)?.details?.price?.final) {
                setSelectedPlan(plan);
            }
        }
    }, [plansWithPrices]);

    const getPlanSticker = (plan: MobilePlanType | undefined) => {
        if (!plan) return null;
        let promo: DiscountType[] = [];
        if (plan.details?.discounts) {
            promo = filterPromotionsByTypesMarketing(plan.details.discounts, [MarketingType.HOME], false, true);
        }
        return <PromoHomeSticker promo={promo[0]} small={false} />;
    };

    let subtitle: ReactNode = capitalize(selectedPlan?.obligationLabel);
    if (selectedPlan.details.price.forever > selectedPlan.details.price.initial) {
        subtitle = (
            <>
                <Text markup={TextMarkup.SPAN}>
                    pendant {getPromoDuration(selectedPlan.details.discounts)} mois puis&nbsp;
                </Text>
                <InlinePrice
                    amount={selectedPlan?.details?.price.forever}
                    period='mois'
                    level={TextLevels.TWO}
                    typo={[]}
                />
                <Text markup={TextMarkup.SPAN}> {selectedPlan?.obligationLabel}</Text>
            </>
        );
    }

    const sortedPlans = useMemo(
        () => [...plansWithPrices].sort((a, b) => (a.dataSize || 0) - (b.dataSize || 0)),
        [plansWithPrices],
    );

    const options = useMemo(
        () =>
            sortedPlans.map((plan) => ({
                id: `${plan.gencode}-${index}`,
                label: plan.dataEnvelope,
                onOptionChange: () => {
                    setSelectedPlan(plan);
                    onSelectedPlan?.(plan);
                },
            })),
        [sortedPlans, index],
    );

    const defaultSelectedOptions = useMemo(
        () => options.find((option) => option.id === `${defaultPlan.gencode}-${index}`)?.id,
        [options, defaultPlan.gencode, index],
    );

    const buttons: ButtonProps[] = [
        {
            children: 'Voir les détails',
            variant: ButtonVariant.SECONDARY,
            onClick: () => setShouldShowModal(true),
        },
    ];

    if (index >= 4) {
        buttons.push({
            children: 'Supprimer',
            accessibilityLabel: 'Supprimer le forfait numéro ' + index,
            onClick: onRemovePlan,
        });
    }

    return (
        <>
            <PlanConfigurator
                title={selectedPlan.name}
                subtitle={subtitle}
                price={selectedPlan?.details?.price?.final ?? 0}
                sticker={getPlanSticker(selectedPlan)}
                options={options}
                defaultSelectedOptions={defaultSelectedOptions}
                fullHeight={fullHeight}
                nuggets={
                    <FlexItem>
                        <Box shadowless>
                            <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                                <OfferCardPepites offer={selectedPlan} />
                            </BoxContent>
                        </Box>
                    </FlexItem>
                }
                buttons={buttons}
                isGrid
            />
            {selectedPlan ? (
                <ModalPlanDetails
                    isOpen={shouldShowModal}
                    onClose={() => setShouldShowModal(false)}
                    plan={selectedPlan}
                    price={{
                        base: 0,
                        final: selectedPlan?.details?.price?.final ?? 0,
                        forever: 0,
                        striked: 0,
                        promoDuration: 0,
                    }}
                />
            ) : null}
        </>
    );
}
